import {useState, useEffect} from 'react'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import {Button as CButton} from '@/products/flexible-web-toolkit/components/button/button'
import type {Block} from '@/services/kontent-ai/types'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'
import type {CtaButtonContentItem} from '@/products/flexible-web-toolkit/blocks/cta-button'
import type {ImageContentItem} from '../../flexible-web-toolkit/blocks/image'
import {ImageBlock} from '../../flexible-web-toolkit/blocks/image'

export type SiteBannerSelfValidationContentItem = IContentItem<{
	title: Elements.TextElement
	content: Elements.RichTextElement
	buttons: Elements.LinkedItemsElement<CtaButtonContentItem>
	popupSuppress: Elements.NumberElement
	selfValidationBannerTextalignment: Elements.MultipleChoiceElement
	selfValidationPopupSize: Elements.MultipleChoiceElement
	siteBannerImageComponent: Elements.LinkedItemsElement<ImageContentItem>
	siteBannerImageAlignment: Elements.MultipleChoiceElement
	enableOpaqueBackground: Elements.MultipleChoiceElement
}>

export const SiteBannerSelfValidationBlock: Block<
	SiteBannerSelfValidationContentItem
> = ({
	block: {
		elements: {
			title,
			content,
			buttons,
			popupSuppress,
			selfValidationBannerTextalignment: alignment,
			selfValidationPopupSize: popupSize,
			siteBannerImageComponent,
			siteBannerImageAlignment: imageAlign,
			enableOpaqueBackground,
		},
	},
	...context
}) => {
	const cookieName = 'bannerCookie'
	const [showBanner, setShowBanner] = useState(true)
	const [currentUrlWithHash, setCurrentUrlWithHash] = useState('')
	const bannerSize = popupSize.length > 0 ? popupSize[0]?.codename : 'medium'

	function setCookie(name: string, value: string, days: number): void {
		const cookieExpiryMilliseconds = days * 24 * 60 * 60 * 1000
		const date = new Date()
		date.setTime(date.getTime() + cookieExpiryMilliseconds)
		document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/`
	}

	// Store the curr
	// ent URL (including anchor) before showing the banner
	useEffect(() => {
		if (typeof window !== 'undefined') {
			const currentUrl = window.location.href
			setCurrentUrlWithHash(currentUrl)
		}
	}, [])

	const handleCtaButtons = (choice: string, cookieExpiry: number): void => {
		if (!choice) {
			document.body.style.overflow = 'visible'
			setShowBanner(false)
			setCookie(cookieName, 'true', cookieExpiry)
			// After closing the banner, restore the URL with anchor (if saved)
			if (currentUrlWithHash) {
				window.location.href = currentUrlWithHash
			}
		} else {
			document.body.style.overflow = 'hidden'
			setShowBanner(true)
		}
	}

	const getImageAlignmentClasses = (
		codename?: string
	): {containerClass: string; innerClass: string} => {
		let containerClass = ''
		let innerClass = ''

		switch (codename) {
			case 'left':
				containerClass = 'block'
				innerClass = ''
				break
			case 'right':
				containerClass = 'flex justify-end'
				innerClass = 'flex justify-end'
				break
			default:
				containerClass = 'flex items-center justify-center'
				innerClass = 'flex justify-center'
				break
		}

		return {containerClass, innerClass}
	}

	const {containerClass, innerClass} = getImageAlignmentClasses(
		imageAlign[0]?.codename
	)

	return (
		<div
			className={clsx(
				`fixed inset-y-0 z-[999] w-full backdrop-filter ${
					!showBanner ? 'hidden' : ''
				} ${
					enableOpaqueBackground[0]?.codename === 'yes'
						? 'backdrop-blur-[20px]'
						: 'backdrop-blur-[5px]'
				}`
			)}
		>
			<div className="m-auto h-screen bg-[rgba(28,28,28,0.76)] px-4 pb-4 pt-20">
				<div
					className={clsx(
						bannerSize === 'large' &&
							`mx-auto my-0 h-[662px] max-w-7xl xxs:h-[650px] xxs:w-[100%] xs:h-[710px] xs:w-[100%]`,
						bannerSize === 'small' &&
							'mx-auto my-0 h-[331px] max-w-2xl xxs:h-[400px] xxs:w-[100%] xs:h-[400px] xs:w-[100%]',
						bannerSize === 'medium' &&
							`mx-auto my-0 h-[495px] max-w-4xl xxs:h-[540px] xxs:w-[100%] xs:h-[540px] xs:w-[100%]`
					)}
					style={{
						// thick needed to mask pointy corners that poke out on chrome! h-[331px] w-[661px]
						border: 'thick solid #F8F8F8',
						backgroundColor: '#F8F8F8',
						borderRadius: '10px',
					}}
				>
					<div className="bg-[#F8F8F8] pb-5 pt-[30px]">
						<div
							className={clsx(
								alignment[0]?.codename === 'left'
									? 'pl-5 pr-5 text-left text-[#373737]'
									: 'pl-5 pr-5 text-center text-[#373737]'
							)}
						>
							<p className="text-2xl font-extrabold leading-[1.18]">
								{title}
							</p>
						</div>
					</div>
					<div
						className={clsx(
							'overflow-y-auto overflow-x-hidden border-t-[1px] border-[lightgrey] bg-[#F8F8F8] pb-[15px] pt-[15px] scrollbar-thin',
							bannerSize === 'large' &&
								'max-h-[470px] xxs:max-h-[410px] xs:max-h-[470px]',
							bannerSize === 'small' && 'max-h-[145px]',
							bannerSize === 'medium' &&
								'max-h-[305px] xxs:max-h-[265px] xs:max-h-[305px]'
						)}
					>
						<RichTextRenderer
							className={clsx(
								alignment[0]?.codename === 'left'
									? 'justify-left pl-5 pr-5 text-left'
									: 'justify-left pl-5 pr-5 text-center text-[#373737]'
							)}
							data-kontent-element-codename="content"
							element={content}
							{...context}
						/>{' '}
						{siteBannerImageComponent[0] ? (
							<div className={containerClass}>
								<div className={innerClass}>
									<ImageBlock
										block={siteBannerImageComponent[0]}
										key={
											siteBannerImageComponent[0].system
												.id
										}
										{...context}
									/>
								</div>
							</div>
						) : null}
					</div>
					<div className="mx-auto my-0 flex w-full justify-center bg-[#F8F8F8] pb-[10px] pt-[10px]  xxs:flex-col xs:flex-col">
						{buttons.map(
							({elements: {url, text}, system: {id}}) => {
								// Append hash to the URL only if it's available
								const updatedUrl = currentUrlWithHash
									? `${url}${window.location.hash}`
									: url

								return (
									<CButton
										className="ml-2 mr-2 w-1/4 xxs:mb-2 xxs:ml-0 xxs:w-full xxs:last:mb-0 xs:mb-2 xs:ml-0 xs:w-full xs:last:mb-0"
										href={updatedUrl}
										key={id}
										onClick={() => {
											handleCtaButtons(
												url,
												popupSuppress ?? 0
											)
										}}
									>
										{text}
									</CButton>
								)
							}
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
